import { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';

import { ReviewType } from '@mvrb-frontend/shared-ui';

import { serializeFormValues } from '../utils/form';
import { AxiosPrivate } from '../utils/axios';
import {
  PublishReviewSchema,
  CreateReviewSchema,
  ReviewStatusEnum,
  WebSiteRoutes,
  ReviewError,
  OrsRoutes,
  autoSavingInterval,
} from '../constants';
import { useGetReview } from '../hooks';

import { ReviewModel, ResponseException } from '../models';
import { getUserId } from '../utils/auth';

export const useReviewForm = props => {
  const { formRef } = props;
  const { type, reviewId } = useParams<{ type: ReviewType; reviewId: string }>();

  const { getReview } = useGetReview();
  const [initialValues, setInitialValues] = useState(new ReviewModel({ type }));
  const [isFetching, setFetching] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [validationSchema, setValidationSchema] = useState(
    initialValues.status == ReviewStatusEnum.DRAFT ? CreateReviewSchema : PublishReviewSchema,
  );
  const [autoSaveTime, setAutoSaveTime] = useState<any>(null);
  const [isEditView, setIsEditView] = useState(false);
  const userId = getUserId();

  useEffect(() => {
    (async () => {
      try {
        if (reviewId) {
          setFetching(true);
          const response = await getReview(reviewId);
          if (response?.data?.review) {
            setInitialValues(new ReviewModel(response.data.review));
          }
          setIsEditView(true);
        }
        setFetching(false);
      } catch (e) {
        const exception = new ResponseException(e);

        if (exception.getErrorType() == ReviewError.REVIEW_NOT_FOUND) {
          window.location.href = OrsRoutes.NotFound;
        } else {
          setErrors(exception.getErrorMessage());
          setFetching(false);
        }
      }
    })();
  }, [reviewId]);

  useEffect(() => {
    setDisplayErrors(!isFetching && !isSending && errors.length > 0);
  }, [isFetching, isSending, errors]);

  const validateForm = async status => {
    const { values, setTouched } = formRef.current;

    if (status) {
      setValidationSchema(status == ReviewStatusEnum.DRAFT ? CreateReviewSchema : PublishReviewSchema);

      const fields = Object.keys(values).reduce((fields, key) => {
        fields[key] = true;
        return fields;
      }, {});

      setTouched(fields);

      await new Promise((resolve, reject) => setTimeout(resolve, 500));
      formRef.current.validateForm(values);
      await new Promise((resolve, reject) => setTimeout(resolve, 500));
    }

    return formRef.current.isValid;
  };

  const submit = async (status: ReviewStatusEnum | undefined = undefined, ignoreNotification = false) => {
    const { values, setFieldValue } = formRef.current;
    const isValid = await validateForm(status);

    if (isValid) {
      try {
        setIsSending(true);
        if (status) {
          setFieldValue('status', status);
          values.status = status;
        }
        // Putting distributionList's properties in an object to match api's requirements - there is room for improvement here
        values.distributionList = {
          id: values.distributionListId,
          distributionListIds: values.distributionListIds,
          userIds: values.distributionUserIds,
          user_id: userId,
        };

        let reviewId = values.id;

        if (values.id) {
          await updateReview({ ...values, ignoreNotification });
        } else {
          reviewId = await createReview(values);
          setFieldValue('id', reviewId);
        }

        setErrors([]);

        if (!status) {
          setAutoSaveTime(new Date());
        }

        if (status == ReviewStatusEnum.ACTIVE) {
          window.location.href = WebSiteRoutes.ReviewDetails.replace(':reviewId', reviewId);
        }
      } catch (e) {
        setErrors(new ResponseException(e).getErrorMessage());
      } finally {
        setIsSending(false);
      }
    }
  };

  const createReview = async (values: any) => {
    const { data: reviewId } = await AxiosPrivate.post('/reviews', serializeFormValues(values));
    const { setFieldValue } = formRef.current;
    setFieldValue('id', reviewId);

    return reviewId;
  };

  const updateReview = async (values: any) => {
    await AxiosPrivate.patch(`/reviews/${values.id}`, serializeFormValues(values));
  };

  const deleteReview = async () => {
    try {
      await AxiosPrivate.delete(`/reviews/${reviewId}`);
      window.location.href = WebSiteRoutes.Reviews;
    } catch (e) {
      setErrors(new ResponseException(e).getErrorMessage());
    }
  };

  const debouncedSave = debounce(() => submit(), 15);

  const autoSave = () => {
    const { dirty, isValid, values } = formRef.current || { dirty: false, isValid: false, values: {} };

    if (dirty && isValid && values.status === ReviewStatusEnum.DRAFT) {
      debouncedSave();
    }
  };

  useEffect(() => {
    const interval = setInterval(() => autoSave(), autoSavingInterval);
    return () => clearInterval(interval);
  }, []);

  return {
    type,
    isEditView,
    initialValues,
    validateForm,
    validationSchema,
    isFetching,
    isSending,
    errors,
    displayErrors,
    autoSaveTime,
    submit,
    deleteReview,
  };
};
